import { NativeSelect } from "@/components/ui/native-select";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { DealPipeline } from "../deals/pipelines";
import { quoteMilestones } from "../specifications/quoteWorkflow";
export function PipelineEditor() {
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "dealPipelines",
  });
  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">
        Порядок этапов определяет колонки доски. Используемый этап или воронку
        можно удалить после переноса сделок, включая архивные.
      </p>
      {fields.map((field, index) => (
        <PipelineFields
          key={field.id}
          index={index}
          remove={() => remove(index)}
          canRemove={fields.length > 1}
        />
      ))}
      <Button
        type="button"
        variant="outline"
        onClick={() =>
          append({
            value: `pipeline-${crypto.randomUUID()}`,
            label: "Новая воронка",
            stages: [
              { value: `stage-${crypto.randomUUID()}`, label: "Новая заявка" },
            ],
            wonStages: [],
            lostStages: [],
          })
        }
      >
        <Plus />
        Добавить воронку
      </Button>
    </div>
  );
}
function PipelineFields({
  index,
  remove,
  canRemove,
}: {
  index: number;
  remove: () => void;
  canRemove: boolean;
}) {
  const { control, register, setValue } = useFormContext();
  const path = `dealPipelines.${index}`;
  const pipeline = useWatch({ control, name: path }) as DealPipeline;
  const workflow = pipeline ? quoteMilestones(pipeline) : undefined;
  const {
    fields,
    append,
    remove: removeStage,
    move,
  } = useFieldArray({ control, name: `${path}.stages` });
  return (
    <section
      data-pipeline-editor
      className="rounded-2xl border p-3 md:p-4 space-y-4"
    >
      <div className="flex items-center gap-2">
        <label className="flex-1 text-sm font-medium">
          Название воронки
          <input
            className="mt-1 w-full rounded-xl border bg-background p-3"
            {...register(`${path}.label`, { required: true })}
          />
        </label>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          disabled={!canRemove}
          aria-label={`Удалить воронку ${pipeline?.label}`}
          onClick={remove}
        >
          <Trash2 />
        </Button>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        {(
          [
            ["sentStage", "КП отправлено"],
            ["agreementStage", "Требуется согласованное КП"],
            ["managerStage", "Можно назначить менеджера"],
          ] as const
        ).map(([key, label]) => (
          <label key={key} className="grid gap-1 text-sm">
            {label}
            <NativeSelect
              aria-label={label}
              value={workflow?.[key] ?? ""}
              onChange={(e) =>
                setValue(
                  `${path}.quoteWorkflow`,
                  { ...workflow, [key]: e.target.value || undefined },
                  { shouldDirty: true },
                )
              }
            >
              <option value="">
                {key === "managerStage" ? "Пока не определён" : "Выберите этап"}
              </option>
              {pipeline?.stages.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </NativeSelect>
          </label>
        ))}
      </div>
      <ol className="space-y-3">
        {fields.map((field, stageIndex) => {
          const stage = pipeline?.stages[stageIndex];
          const outcome = pipeline?.wonStages.includes(stage?.value)
            ? "won"
            : pipeline?.lostStages.includes(stage?.value)
              ? "lost"
              : "active";
          return (
            <li
              data-pipeline-stage
              key={field.id}
              className="flex flex-wrap gap-2 items-center"
            >
              <span className="text-xs text-muted-foreground w-5">
                {stageIndex + 1}
              </span>
              <input
                aria-label={`Название этапа ${stageIndex + 1}`}
                className="min-w-32 flex-1 rounded-xl border bg-background p-2"
                {...register(`${path}.stages.${stageIndex}.label`, {
                  required: true,
                })}
              />
              <NativeSelect
                aria-label={`Результат этапа ${stageIndex + 1}`}
                value={outcome}
                className="rounded-xl border bg-background p-2 text-sm"
                onChange={(event) => {
                  for (const [key, selected] of [
                    ["wonStages", "won"],
                    ["lostStages", "lost"],
                  ] as const)
                    setValue(
                      `${path}.${key}`,
                      [
                        ...pipeline[key].filter(
                          (value) => value !== stage.value,
                        ),
                        ...(event.target.value === selected
                          ? [stage.value]
                          : []),
                      ],
                      { shouldDirty: true },
                    );
                }}
              >
                <option value="active">В работе</option>
                <option value="won">Успешно завершён</option>
                <option value="lost">Закрыт без продажи</option>
              </NativeSelect>
              <div className="flex">
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label={`Этап ${stageIndex + 1} вверх`}
                  disabled={stageIndex === 0}
                  onClick={() => move(stageIndex, stageIndex - 1)}
                >
                  <ArrowUp />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label={`Этап ${stageIndex + 1} вниз`}
                  disabled={stageIndex === fields.length - 1}
                  onClick={() => move(stageIndex, stageIndex + 1)}
                >
                  <ArrowDown />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label={`Удалить этап ${stageIndex + 1}`}
                  disabled={fields.length === 1}
                  onClick={() => {
                    removeStage(stageIndex);
                    for (const key of ["wonStages", "lostStages"] as const)
                      setValue(
                        `${path}.${key}`,
                        pipeline[key].filter((value) => value !== stage.value),
                        { shouldDirty: true },
                      );
                  }}
                >
                  <Trash2 />
                </Button>
              </div>
            </li>
          );
        })}
      </ol>
      <Button
        type="button"
        variant="outline"
        onClick={() =>
          append({ value: `stage-${crypto.randomUUID()}`, label: "Новый этап" })
        }
      >
        <Plus />
        Добавить этап
      </Button>
    </section>
  );
}
